import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import QRCode from 'qrcode';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ouropix/ui';
import { Button } from '@ouropix/ui';
import { Input } from '@ouropix/ui';
import { Alert, AlertDescription } from '@ouropix/ui';
import {
  Shield, ShieldCheck, Copy, Loader2, AlertTriangle, Check, Download,
} from 'lucide-react';
import { firstSetup2FA, firstEnable2FA } from '@/lib/auth';
import { useAuthStore } from '@/stores/auth';
import { cn } from '@/lib/utils';

// Storage key para preservar tempToken entre refreshes (sobrevive a F5 acidental)
const TEMP_TOKEN_KEY = 'ouropix_admin_mfa_setup_tempToken';
const USER_ID_KEY = 'ouropix_admin_mfa_setup_userId';

interface RouteSearch {
  userId?: string;
  tempToken?: string;
}

export const Route = createFileRoute('/setup-2fa')({
  validateSearch: (search: Record<string, unknown>): RouteSearch => ({
    userId: typeof search.userId === 'string' ? search.userId : undefined,
    tempToken: typeof search.tempToken === 'string' ? search.tempToken : undefined,
  }),
  beforeLoad: ({ search }) => {
    // Preferência: tempToken via query string. Fallback: sessionStorage.
    const token = search.tempToken ?? sessionStorage.getItem(TEMP_TOKEN_KEY);
    if (!token) {
      throw redirect({ to: '/login' });
    }
  },
  component: Setup2FAPage,
});

type Step = 'welcome' | 'setup' | 'verify' | 'done';

function Setup2FAPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [tempToken] = useState<string>(() => {
    if (search.tempToken) {
      sessionStorage.setItem(TEMP_TOKEN_KEY, search.tempToken);
      return search.tempToken;
    }
    return sessionStorage.getItem(TEMP_TOKEN_KEY) ?? '';
  });
  const [userId] = useState<string>(() => {
    if (search.userId) {
      sessionStorage.setItem(USER_ID_KEY, search.userId);
      return search.userId;
    }
    return sessionStorage.getItem(USER_ID_KEY) ?? '';
  });

  const [step, setStep] = useState<Step>('welcome');
  const [setupData, setSetupData] = useState<{
    secret: string;
    qrCodeUrl: string;
    backupCodes: string[];
  } | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [backupAcknowledged, setBackupAcknowledged] = useState(false);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Cleanup storage ao desmontar com sucesso
    return () => {
      if (step === 'done') {
        sessionStorage.removeItem(TEMP_TOKEN_KEY);
        sessionStorage.removeItem(USER_ID_KEY);
      }
    };
  }, [step]);

  const handleStart = async () => {
    setLoading(true);
    try {
      const result = await firstSetup2FA(tempToken);
      if (!result.success || !result.secret || !result.qrCodeUrl || !result.backupCodes) {
        toast.error(result.message || 'Falha ao gerar chave 2FA. Tente fazer login novamente.');
        navigate({ to: '/login' });
        return;
      }
      setSetupData({
        secret: result.secret,
        qrCodeUrl: result.qrCodeUrl,
        backupCodes: result.backupCodes,
      });
      // G-05: gerar QR localmente (nunca enviar otpauth para serviço externo)
      const dataUrl = await QRCode.toDataURL(result.qrCodeUrl, {
        width: 240,
        margin: 2,
        color: { dark: '#000000', light: '#ffffff' },
      });
      setQrDataUrl(dataUrl);
      setStep('setup');
    } catch {
      toast.error('Erro ao iniciar configuração. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      toast.error('Digite o código de 6 dígitos');
      return;
    }
    setLoading(true);
    try {
      const result = await firstEnable2FA({ tempToken, code });
      if (!result.success) {
        toast.error(result.message || 'Código inválido. Tente novamente.');
        setCode('');
        return;
      }
      setStep('done');
      toast.success('2FA ativado com sucesso!');
      sessionStorage.removeItem(TEMP_TOKEN_KEY);
      sessionStorage.removeItem(USER_ID_KEY);
      // Limpa store Zustand + storage legado pra forçar fluxo completo de
      // login. Sem isso, JWT velho (sem `sid` da nova sessao 2FA) persiste
      // no browser e endpoints step-up retornam "Sessao invalida".
      useAuthStore.getState().logout();
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user_id');
      setTimeout(() => navigate({ to: '/login' }), 2000);
    } catch {
      toast.error('Falha ao ativar 2FA. Tente novamente.');
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado`);
  };

  const downloadBackupCodes = () => {
    if (!setupData) return;
    const content = [
      'OuroPix Admin — Códigos de Backup 2FA',
      `Gerado em: ${new Date().toLocaleString('pt-BR')}`,
      `Admin ID: ${userId}`,
      '',
      'IMPORTANTE:',
      '- Cada código só pode ser usado UMA vez.',
      '- Guarde em local seguro (cofre, gerenciador de senhas).',
      '- Use apenas se perder acesso ao app autenticador.',
      '',
      'Códigos:',
      ...setupData.backupCodes.map((c, i) => `${i + 1}. ${c}`),
    ].join('\n');
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ouropix-admin-backup-codes-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Render: Welcome ─────────────────────────────────────────
  if (step === 'welcome') {
    return (
      <WizardLayout>
        <Card className="w-full max-w-lg">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-primary/10">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle>Ative a autenticação de 2 fatores</CardTitle>
                <CardDescription className="mt-1">
                  Sua conta administrativa requer 2FA para acessar o painel.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertDescription className="text-sm">
                O 2FA é <strong>obrigatório</strong> para contas admin e protege operações críticas
                como aprovação de saques e ajustes de saldo. Você precisará de um app autenticador
                (Google Authenticator, Authy, 1Password ou similar).
              </AlertDescription>
            </Alert>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p className="font-medium text-foreground">Você vai precisar de:</p>
              <ul className="space-y-1 pl-4 list-disc">
                <li>Um smartphone com app autenticador instalado</li>
                <li>Cerca de 2 minutos</li>
                <li>Um local seguro para guardar os códigos de backup</li>
              </ul>
            </div>
            <Button onClick={handleStart} disabled={loading} className="w-full">
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Gerando chave...
                </>
              ) : (
                'Configurar agora'
              )}
            </Button>
          </CardContent>
        </Card>
      </WizardLayout>
    );
  }

  // ── Render: Setup (QR + secret + backup codes) ──────────────
  if (step === 'setup' && setupData) {
    return (
      <WizardLayout>
        <Card className="w-full max-w-2xl">
          <CardHeader>
            <CardTitle>Passo 1: Configure o app autenticador</CardTitle>
            <CardDescription>
              Escaneie o QR Code abaixo no Google Authenticator, Authy ou similar.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* QR Code */}
              <div className="space-y-3">
                <div className="flex items-center justify-center p-6 bg-white rounded-xl">
                  {qrDataUrl ? (
                    <img
                      src={qrDataUrl}
                      alt="QR Code 2FA"
                      width={240}
                      height={240}
                    />
                  ) : (
                    <div className="w-[240px] h-[240px] flex items-center justify-center">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  Escaneie com seu app autenticador
                </p>
              </div>

              {/* Manual entry */}
              <div className="space-y-3">
                <p className="text-sm font-medium">Ou insira manualmente:</p>
                <div className="flex items-start gap-2">
                  <code className="flex-1 p-3 bg-muted rounded-lg text-xs break-all font-mono select-all">
                    {setupData.secret}
                  </code>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => copyToClipboard(setupData.secret, 'Secret')}
                    className="shrink-0"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Nome: <strong>OuroPix Admin</strong>
                </p>
              </div>
            </div>

            <div className="h-px bg-border" />

            {/* Backup codes */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-400" />
                <h3 className="font-semibold">Códigos de backup</h3>
              </div>
              <Alert variant="destructive" className="bg-orange-500/10 border-orange-500/30">
                <AlertDescription className="text-sm">
                  <strong>Anote estes 8 códigos agora — eles aparecerão apenas uma vez.</strong>
                  {' '}Cada código pode ser usado uma única vez caso você perca acesso ao app autenticador.
                </AlertDescription>
              </Alert>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-4 bg-muted/50 rounded-lg font-mono text-sm">
                {setupData.backupCodes.map((c, i) => (
                  <div key={c} className="flex items-center gap-1.5">
                    <span className="text-muted-foreground text-xs">{i + 1}.</span>
                    <code className="select-all">{c}</code>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(setupData.backupCodes.join('\n'), 'Códigos')}
                >
                  <Copy className="mr-2 h-3.5 w-3.5" />
                  Copiar todos
                </Button>
                <Button variant="outline" size="sm" onClick={downloadBackupCodes}>
                  <Download className="mr-2 h-3.5 w-3.5" />
                  Baixar .txt
                </Button>
              </div>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={backupAcknowledged}
                  onChange={(e) => setBackupAcknowledged(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  Confirmo que anotei/salvei os códigos de backup em local seguro e entendo que
                  não poderei vê-los novamente.
                </span>
              </label>
            </div>

            <Button
              onClick={() => setStep('verify')}
              disabled={!backupAcknowledged}
              className="w-full"
            >
              Continuar para verificação
            </Button>
          </CardContent>
        </Card>
      </WizardLayout>
    );
  }

  // ── Render: Verify ──────────────────────────────────────────
  if (step === 'verify') {
    return (
      <WizardLayout>
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Passo 2: Confirme o código</CardTitle>
            <CardDescription>
              Digite o código de 6 dígitos exibido no seu app autenticador.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleVerify} className="space-y-4">
              <Input
                id="totp-verify"
                type="text"
                inputMode="numeric"
                maxLength={6}
                autoComplete="one-time-code"
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                disabled={loading}
                className={cn('h-14 text-center text-2xl font-mono tracking-[0.5em]')}
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep('setup')}
                  disabled={loading}
                  className="flex-1"
                >
                  Voltar
                </Button>
                <Button type="submit" disabled={loading || code.length !== 6} className="flex-1">
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Ativando...
                    </>
                  ) : (
                    'Ativar 2FA'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </WizardLayout>
    );
  }

  // ── Render: Done ────────────────────────────────────────────
  return (
    <WizardLayout>
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col items-center gap-4 py-10">
          <div className="p-3 rounded-full bg-green-500/10">
            <ShieldCheck className="h-10 w-10 text-green-400" />
          </div>
          <div className="text-center space-y-1">
            <h2 className="text-xl font-semibold">2FA ativado com sucesso!</h2>
            <p className="text-sm text-muted-foreground">
              Redirecionando para login em instantes...
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Check className="h-3.5 w-3.5 text-green-400" />
            Faça login novamente usando seu app autenticador.
          </div>
        </CardContent>
      </Card>
    </WizardLayout>
  );
}

function WizardLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background to-muted p-4">
      {children}
    </div>
  );
}
